import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import useAuthStore from "@/store/use-auth-store";
import { isMobile } from "@/lib/is-mobile";
import { clientSource } from "@/lib/client-source";
import { gb, getOrCreateDeviceId } from "@/lib/growthbook";

const MS_PER_DAY = 1000 * 60 * 60 * 24;

function daysSince(iso?: string): number | undefined {
  if (!iso) return undefined;
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return undefined;
  return Math.floor((Date.now() - ts) / MS_PER_DAY);
}

/**
 * GrowthBook targeting attribute'larını kullanıcı state'iyle senkron tutar.
 *
 * `App` içinde bir kez mount edilir, hiçbir şey render etmez.
 *
 * Dikkat: `setAttributes` mevcut attribute'ların ÜZERİNE YAZMAZ, hepsini
 * DEĞİŞTİRİR. GrowthBook'ta `updateAttributes` diye bir şey yok — bu yüzden
 * burada her seferinde objenin tamamı yeniden kuruluyor. Parça parça set etmek
 * yazılmayan attribute'ları sessizce siler ve o attribute'a bakan kural
 * kimseyle eşleşmez.
 *
 * GrowthBook UI'daki attribute listesi ve `apps/api`'deki `FlagAttributes` ile
 * aynı isimlerde kalmalı.
 */
export function GrowthBookAttributesSync(): null {
  const { i18n } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const language = i18n.language;

  useEffect(() => {
    const deviceId = getOrCreateDeviceId();

    gb.setAttributes({
      // Hash attribute: bucketing bunun üzerinden yapılır. Login öncesi cihaz,
      // sonrası kullanıcı — ikisi arasındaki geçişi sticky bucketing tutuyor.
      id: user?._id ?? deviceId,
      deviceId,
      loggedIn: !!user?._id,
      // ISO-2. Kayıt sırasında IP geo'dan doldurulur (apps/api). Geo başarısız
      // olduysa gerçekten yok — varsayılan bir ülke koyma, yoksa ülke kuralı
      // konumu bilinmeyen kullanıcılarla eşleşmeye başlar.
      country: user?.region || undefined,
      language,
      deviceType: isMobile() ? "mobile" : "desktop",
      // Hangi istemci: `web` ya da `iscep`. Hem hedefleme için (bir deney tek
      // bir storefront'a kısıtlanabilsin), hem de `apps/api`'deki
      // `FlagAttributes.source` ile aynı isimde kalsın diye — iki taraftan
      // birinin göndermediği bir attribute'a yazılan kural kimseyle eşleşmez
      // ve bu, kapalı bir kuraldan ayırt edilemez.
      source: clientSource(),
      appEnv: import.meta.env.VITE_APP_ENV,
      daysSinceSignup: daysSince(user?.createdAt),
    });
  }, [user, language]);

  return null;
}
